import uuid from 'helpers/uuid'
import theme from 'theme/index'

import {
  getDefaultQuantityForOrderItem, getDeliveredForOrderItem, getOrderedForOrderItem,
  getOutstandingForOrderItem,
  getReadyForDeliveryForOrderItem, getReturnedForOrderItem, getScheduledForOrderItem,
} from 'helpers/deliveries/index'
import {deliveriesSyncVersion} from 'helpers/constants'
import {getAllSubItems, isStockableItem, orderItemIsBuiltType} from 'helpers/orders'

export const getDeliveryNoteItems = (user, order, isReturnNote = false, search) => {

  const orderItems = (order.orderitems || [])
  const jobCards = (order.jobCards || [])
  const deliveryNotes = (order.deliveryNotes || [])

  return getDeliveryNoteItemsForOrderItems(user, order, orderItems, deliveryNotes, jobCards, isReturnNote, search)
}

export const getDeliveryNoteItemsForDelivery = (user, order, isReturnNote = false, search) => {

  const orderItems = (order.orderitems || [])
  const jobCards = (order.jobCards || [])
  const deliveryNotes = (order.deliveryNotes || [])

  return getDeliveryNoteItemsForOrderItemsForDelivery(user, order, orderItems, deliveryNotes, jobCards, isReturnNote, search)
}

export const getDeliveryNoteItemsForOrderItems = (user, order, orderItems, deliveryNotes, jobCards, isReturnNote = false, search) =>
    filterOrderItems(orderItems, search)
        .map(orderItem => getDeliveryNoteItemForOrderItem(user, order, orderItem, deliveryNotes, jobCards, isReturnNote))

export const getDeliveryNoteItemsForOrderItemsForDelivery = (user, order, orderItems, deliveryNotes, jobCards, search) =>
    filterOrderItems(orderItems, search)
        .map(orderItem => getDeliveryNoteItemForOrderItemForDelivery(user, order, orderItem, deliveryNotes, jobCards))


export const filterOrderItems = (orderItems, search) => {
  return orderItems.filter(orderItem => !orderItem.dormant
      && (search ? orderItem.desc.toLowerCase().includes(search.toLowerCase())
              || orderItem.partCode.toLowerCase().includes(search.toLowerCase())
              : true
          ))
}

export const getDeliveryNoteItemForOrderItem = (user, order, orderItem, deliveryNotes, jobCards, isReturnNote = false) => ({
  id: uuid(),
  isNew: true,
  itemId: orderItem.lineItem,
  itemLetter: orderItem.lineItemLetter,
  orderItemId: orderItem.id,
  description: orderItem.desc,
  quantity: getDefaultQuantityForOrderItem(user, order, orderItem, deliveryNotes, jobCards, isReturnNote),
  createdDate: new Date().getTime(),
  modifiedDate: new Date().getTime(),
  deliveryItemTotals: calculateTotalsForOrderItem(user, order, orderItem, deliveryNotes, jobCards),
  orderItem,
})

export const getDeliveryNoteItemForOrderItemForDelivery = (user, order, orderItem, deliveryNotes, jobCards) => ({
  id: uuid(),
  isNew: true,
  itemId: orderItem.lineItem,
  itemLetter: orderItem.lineItemLetter,
  orderItemId: orderItem.id,
  description: orderItem.desc,
  quantity: getOutstandingForOrderItem(user, order, orderItem, deliveryNotes),
  createdDate: new Date().getTime(),
  modifiedDate: new Date().getTime(),
  deliveryItemTotals: calculateTotalsForOrderItem(user, order, orderItem, deliveryNotes, jobCards),
  orderItem,
})

export const DeliveryItemStatus = {
  PENDING: 'PENDING',
  READY_FOR_DELIVERY: 'READY_FOR_DELIVERY',
  SCHEDULED: 'SCHEDULED',
  DELIVERED: 'DELIVERED',
}

export const calculateTotalsForOrderItem = (user, order, orderItem, deliveryNotes, jobCards) => {
  return {
    totalOrdered: getOrderedForOrderItem(orderItem),
    totalReadyForDelivery: getReadyForDeliveryForOrderItem(user, order, null, orderItem, jobCards, deliveryNotes),
    totalScheduled: getScheduledForOrderItem(orderItem, deliveryNotes),
    totalDelivered: getDeliveredForOrderItem(orderItem, deliveryNotes),
    totalReturned: getReturnedForOrderItem(user, orderItem, deliveryNotes),
    totalOutstanding: getOutstandingForOrderItem(user, order, orderItem, deliveryNotes),
  }
}

export const getDeliveryItemStatus = (totalsForDeliveryItem) => {
  const {
    totalOrdered,
    totalReadyForDelivery,
    totalScheduled,
    totalDelivered,
  } = totalsForDeliveryItem

  if (totalDelivered >= totalOrdered)
    return DeliveryItemStatus.DELIVERED

  if (totalScheduled >= totalOrdered)
    return DeliveryItemStatus.SCHEDULED

  if (totalReadyForDelivery >= totalOrdered)
    return DeliveryItemStatus.READY_FOR_DELIVERY

  return DeliveryItemStatus.PENDING
}

export const getColorForDeliveryItem = (deliveryItemStatus) => {
  switch (deliveryItemStatus) {
    case DeliveryItemStatus.PENDING:
      return theme.palette.deliveryItemStatus.pending
    case DeliveryItemStatus.READY_FOR_DELIVERY:
      return theme.palette.deliveryItemStatus.readyForDelivery
    case DeliveryItemStatus.SCHEDULED:
      return theme.palette.deliveryItemStatus.scheduled
    case DeliveryItemStatus.DELIVERED:
      return theme.palette.deliveryItemStatus.delivered
    default:
      return theme.palette.deliveryItemStatus.pending
  }
}

export const getStatusForDeliveryItem = (deliveryItemStatus, productionCompleteName) => {
  switch (deliveryItemStatus) {
    case DeliveryItemStatus.PENDING:
      return 'Pending'
    case DeliveryItemStatus.READY_FOR_DELIVERY:
      return productionCompleteName
    case DeliveryItemStatus.SCHEDULED:
      return 'Scheduled'
    case DeliveryItemStatus.DELIVERED:
      return 'Delivered'
    default:
      return 'Pending'
  }
}

export const isMoreThanAllocated = (user, deliveryNoteItem) => {
  return user.proSync &&
      user.proVersion >= deliveriesSyncVersion &&
      isStockableItem(deliveryNoteItem.orderItem) &&
      deliveryNoteItem.quantity > deliveryNoteItem.orderItem.stockAllocated
}

export const isDeliveringMoreThanAllocated = (user, deliveryNoteItems) => {
  return deliveryNoteItems.some(item => isMoreThanAllocated(user, item))
}

export const isMoreThanPicked = (user, deliveryNoteItem) => {
  return user.proSync &&
      user.proVersion >= deliveriesSyncVersion &&
      user.settings.pickingMode &&
      isStockableItem(deliveryNoteItem.orderItem) &&
      deliveryNoteItem.quantity > deliveryNoteItem.orderItem.stockPicked
}

export const isDeliveringMoreThanPicked = (user, deliveryNoteItems) => {
  return deliveryNoteItems.some(item => isMoreThanPicked(user, item))
}

export const getAllocatedStockLabel = (orderItem) => {
  return orderItemIsBuiltType(orderItem)
    ? `${getAllocatedStockForOrderItem(orderItem)}/${getTotalStockQuantityForSubItems(orderItem)}`
    : isStockableItem(orderItem)
        ? `${orderItem.stockAllocated || 0}`
        : 'N/A'
}

export const getAllocatedStockForOrderItem = (orderItem) => {
  return orderItemIsBuiltType(orderItem)
    ? getAllocatedStockForSubItems(orderItem)
    : isStockableItem(orderItem)
        ? orderItem.stockAllocated || 0
        : 0
}

export const getPickedStockLabel = (orderItem) => {
  return orderItemIsBuiltType(orderItem)
      ? `${getPickedStockForOrderItem(orderItem)}/${getTotalStockQuantityForSubItems(orderItem)}`
      : isStockableItem(orderItem)
          ? `${orderItem.stockPicked || 0}`
          : 'N/A'
}

export const getPickedStockForOrderItem = (orderItem) => {
  return orderItemIsBuiltType(orderItem)
      ? getPickedStockForSubItems(orderItem)
      : isStockableItem(orderItem)
        ? orderItem.stockPicked || 0
        : 0
}

export const getAllocatedStockForSubItems = (orderItem) =>
  getAllSubItems(orderItem).reduce((total, subItem) => total + (isStockableItem(subItem) ? (subItem.stockAllocated || 0) : 0), 0)

export const getPickedStockForSubItems = (orderItem) =>
  getAllSubItems(orderItem).reduce((total, subItem) => total + (isStockableItem(subItem) ? (subItem.stockPicked || 0) : 0), 0)

export const getTotalStockQuantityForSubItems = (orderItem, parentQuantity = 1) =>
    orderItemIsBuiltType(orderItem)
      ? orderItem.subItems.reduce((total, subItem) => total + getTotalStockQuantityForSubItems(subItem, orderItem.quantity * parentQuantity), 0)
      : isStockableItem(orderItem)
          ? orderItem.quantity * parentQuantity
          : 0

export const isDeliveringMoreThanAllocatedSubItems = (user, deliveryNoteItems) => {
  return deliveryNoteItems.some(item => isMoreThanAllocatedSubItems(user, item))
}

export const isMoreThanAllocatedSubItems = (user, deliveryNoteItem) => {
  return user.proSync &&
      user.proVersion >= deliveriesSyncVersion &&
      orderItemIsBuiltType(deliveryNoteItem.orderItem) &&
      isMoreThanAllocatedSubItem(deliveryNoteItem.orderItem, deliveryNoteItem.quantity)
}

export const isMoreThanAllocatedSubItem = (orderItem, quantityRequired) => {
  return orderItemIsBuiltType(orderItem)
    ? orderItem.subItems.some(subItem => isMoreThanAllocatedSubItem(subItem, subItem.quantity * quantityRequired))
    : isStockableItem(orderItem)
      ? quantityRequired > orderItem.stockAllocated
      : false
}

export const isDeliveringZeroAllocatedSubItems = (user, deliveryNoteItems) => {
  return deliveryNoteItems.some(item => item.quantity > 0 && isZeroAllocatedSubItems(user, item))
}

export const isZeroAllocatedSubItems = (user, deliveryNoteItem) => {
  return user.proSync &&
      user.proVersion >= deliveriesSyncVersion &&
      orderItemIsBuiltType(deliveryNoteItem.orderItem) &&
      isZeroAllocatedSubItem(deliveryNoteItem.orderItem)
}

export const isZeroAllocatedSubItem = (orderItem) => {
  return orderItemIsBuiltType(orderItem)
      ? orderItem.subItems.every(subItem => isZeroAllocatedSubItem(subItem))
      : isStockableItem(orderItem)
          ? orderItem.stockAllocated === 0
          : true
}

export const isDeliveringMoreThanPickedSubItems = (user, deliveryNoteItems) => {
  return deliveryNoteItems.some(item => isMoreThanPickedSubItems(user, item))
}

export const isMoreThanPickedSubItems = (user, deliveryNoteItem) => {
  return user.proSync &&
      user.proVersion >= deliveriesSyncVersion &&
      user.settings.pickingMode &&
      orderItemIsBuiltType(deliveryNoteItem.orderItem) &&
      isMoreThanPickedSubItem(deliveryNoteItem.orderItem, deliveryNoteItem.quantity)
}

export const isMoreThanPickedSubItem = (orderItem, quantityRequired) => {
  return orderItemIsBuiltType(orderItem)
      ? orderItem.subItems.some(subItem => isMoreThanPickedSubItem(subItem, subItem.quantity * quantityRequired))
      : isStockableItem(orderItem)
          ? quantityRequired > orderItem.stockPicked
          : false
}

export const isDeliveringZeroPickedSubItems = (user, deliveryNoteItems) => {
  return deliveryNoteItems.some(item => item.quantity > 0 && isZeroPickedSubItems(user, item))
}

export const isZeroPickedSubItems = (user, deliveryNoteItem) => {
  return user.proSync &&
      user.proVersion >= deliveriesSyncVersion &&
      user.settings.pickingMode &&
      orderItemIsBuiltType(deliveryNoteItem.orderItem) &&
      isZeroPickedSubItem(deliveryNoteItem.orderItem)
}

export const isZeroPickedSubItem = (orderItem) => {
  return orderItemIsBuiltType(orderItem)
      ? orderItem.subItems.every(subItem => isZeroPickedSubItem(subItem))
      : isStockableItem(orderItem)
          ? orderItem.stockPicked === 0
          : true
}
